// The condition preview's evaluator (spec/dashboard.md section 7.1, "Preview as player"): the truth tables and leaves of
// spec/liveops-engine.md section 5.1 read against one player's saved profiles, a chosen game time and the session facts the
// dashboard knows. The server has no condition evaluator (segment membership is read from the players' profiles), so this
// is the dashboard's reading of the engine rules; the game decides. Pure: everything comes in through the context.
import type { Json } from '../../api/types';
import type { DocumentLike } from '../format';
import { CONDITION_NS } from './model';

export type EvalState = 'pass' | 'fail' | 'delayed' | 'unknown';

export interface LeafVerdict {
  state: EvalState;
  reason?: string;
}

export interface EvalResult {
  state: EvalState;
  /** every node evaluated against the user (not the per-element runs inside a list filter), by node id */
  leaves: Map<string, LeafVerdict>;
}

export interface SessionFacts {
  appVersion?: string;
  engineVersion?: string;
  platform?: string | number;
  balancyPlatform?: string | number;
  language?: string;
  country?: string;
}

export interface EvalContext {
  /** the catalog, drafts included */
  resolve: (id: string) => DocumentLike | undefined;
  /** the player's collection for a profile root (`SmartObjects.UnnyProfile` ↔ `UnnyProfile`), as `{ key: value }` */
  profiles: (templateName: string) => Json | undefined;
  /** game-time unix seconds (section 3: server time + the environment's offset) */
  now: number;
  /** server unix seconds for the Revenue window (section 3); `now` when not given */
  serverNow?: number;
  session: SessionFacts;
  /** a product reference of a ProductWasPurchased leaf → the store product ids a purchase records */
  product?: (id: string) => { itemId?: string | null } | undefined;
}

const DAY = 86400;
const SYSTEM_PROFILE = 'SmartObjects.UnnyProfile';

type Obj = Record<string, Json>;
const obj = (v: unknown): Obj | null => (v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : null);
const arr = (v: unknown): Json[] => (Array.isArray(v) ? (v as Json[]) : []);

const verdict = (state: EvalState, reason?: string): LeafVerdict => (reason === undefined ? { state } : { state, reason });
const bool = (b: boolean): LeafVerdict => verdict(b ? 'pass' : 'fail');

// ---- helpers (exported for the tests) ----------------------------------------------------------------------------------

/** The legacy `Utils.Compare` (ComparisonType 0 ==, 1 >, 2 >=, 3 <, 4 <=, 5 !=); 6 isNullOrEmpty is not numeric and is false. */
export function compare(op: number, a: number, b: number): boolean {
  switch (op) {
    case 0:
      return a === b;
    case 1:
      return a > b;
    case 2:
      return a >= b;
    case 3:
      return a < b;
    case 4:
      return a <= b;
    case 5:
      return a !== b;
    default:
      return false;
  }
}

/**
 * Reads a dotted PascalCase path (`GeneralInfo.Level`, `Inventories.Items.Slots`) case-insensitively from a profile object
 * keyed by its stored camelCase names; `[n]` indexes a list. `found: false` when a step is missing.
 */
export function readPath(root: unknown, path: string): { found: boolean; value: Json | undefined } {
  let cur: unknown = root;
  const steps = path.split('.').filter((s) => s !== '');
  for (const raw of steps) {
    const m = /^([^[\]]*)((?:\[\d+\])*)$/.exec(raw);
    const name = m ? m[1]! : raw;
    const indexes = m && m[2] ? Array.from(m[2].matchAll(/\[(\d+)\]/g), (x) => Number(x[1])) : [];
    if (name !== '') {
      const o = obj(cur);
      if (!o) return { found: false, value: undefined };
      const lower = name.toLowerCase();
      const key = Object.keys(o).find((k) => k.toLowerCase() === lower);
      if (key === undefined) return { found: false, value: undefined };
      cur = o[key];
    }
    for (const i of indexes) {
      if (!Array.isArray(cur) || i >= cur.length) return { found: false, value: undefined };
      cur = cur[i];
    }
  }
  return { found: true, value: cur as Json };
}

/** `System.Version` parsing: 2–4 non-negative integer parts; a bare `2` reads as `2.0` (the SDK's rule); null when unparsable. */
export function parseVersion(text: unknown): number[] | null {
  if (typeof text !== 'string' && typeof text !== 'number') return null;
  let t = String(text).trim();
  if (t === '') return null;
  if (!t.includes('.')) t += '.0';
  const parts = t.split('.');
  if (parts.length < 2 || parts.length > 4) return null;
  const nums = parts.map((p) => (/^\d+$/.test(p.trim()) ? Number(p.trim()) : NaN));
  return nums.some((n) => Number.isNaN(n) || n > 2147483647) ? null : nums;
}

/** `System.Version.CompareTo`: part by part, a missing build or revision is below 0 (1.0 < 1.0.0); null when either is unparsable. */
export function versionCompare(a: unknown, b: unknown): number | null {
  const x = parseVersion(a);
  const y = parseVersion(b);
  if (!x || !y) return null;
  for (let i = 0; i < 4; i++) {
    const p = x[i] ?? -1;
    const q = y[i] ?? -1;
    if (p !== q) return p < q ? -1 : 1;
  }
  return 0;
}

/** Ordinal string comparison (UTF-16 code units, as `string.CompareOrdinal`): −1, 0, 1. */
export function ordinal(a: string, b: string): number {
  return a === b ? 0 : a < b ? -1 : 1;
}

/** A number as the engine reads one invariantly (numbers and numeric strings); null for null, bools, objects. */
export function toNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '' && /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(v.trim())) return Number(v);
  return null;
}

/** An instant field of a time leaf: unix seconds, a numeric string, or an ISO text. */
function instantOf(v: unknown): number {
  const n = toNumber(v);
  if (n !== null) return n;
  if (typeof v === 'string') {
    const ms = Date.parse(v);
    if (!Number.isNaN(ms)) return Math.floor(ms / 1000);
  }
  const o = obj(v);
  if (o && 'unixSeconds' in o) return toNumber(o.unixSeconds) ?? 0;
  return 0;
}

/** `HH:MM` → minutes (`24:00` = 1440); an unparsable part reads 0 and is reported (section 5.1). */
function minutesOfDay(text: unknown, field: string, notes: string[]): number {
  if (typeof text !== 'string' || text.trim() === '') {
    notes.push(`${field} is empty; read as 00:00`);
    return 0;
  }
  const parts = text.split(':');
  if (parts.length < 2) {
    notes.push(`${field} '${text}' is not HH:MM; read as 00:00`);
    return 0;
  }
  const h = /^\s*\d+\s*$/.test(parts[0]!) ? Number(parts[0]) : (notes.push(`${field} '${text}' has a part that is not a number; read as 0`), 0);
  const m = /^\s*\d+\s*$/.test(parts[1]!) ? Number(parts[1]) : (notes.push(`${field} '${text}' has a part that is not a number; read as 0`), 0);
  return Math.max(0, h * 60 + m);
}

const shortName = (name: string) => name.split('.').pop() ?? name;

// ---- the evaluator -----------------------------------------------------------------------------------------------------

interface Run {
  ctx: EvalContext;
  leaves: Map<string, LeafVerdict>;
}

/** Evaluates the tree under `rootId` for the player of `ctx` (a null root passes, section 5.1). */
export function evaluateCondition(rootId: string | null, ctx: EvalContext): EvalResult {
  const run: Run = { ctx, leaves: new Map() };
  if (!rootId) return { state: 'pass', leaves: run.leaves };
  const r = evalNode(run, rootId, null, 0);
  return { state: r.state, leaves: run.leaves };
}

function evalNode(run: Run, id: string, element: Obj | null, depth: number): LeafVerdict {
  let out: LeafVerdict;
  if (depth > 32) out = verdict('unknown', 'the tree is too deep');
  else {
    const node = run.ctx.resolve(id);
    if (!node) out = verdict('unknown', 'the node has not loaded');
    else {
      try {
        out = evalCore(run, node, element, depth);
      } catch (e) {
        out = verdict('fail', e instanceof Error ? e.message : String(e));
      }
    }
  }
  if (element === null) run.leaves.set(id, out);
  return out;
}

/** And: a fail wins, then unknown, then delayed; empty passes. Or: a pass wins, then unknown, then delayed; empty fails. */
function combine(run: Run, kids: string[], element: Obj | null, depth: number, and: boolean): LeafVerdict {
  let delayed = false;
  let unknown = false;
  let decided = false;
  for (const c of kids) {
    const r = evalNode(run, c, element, depth + 1);
    if (decided) continue;
    if (r.state === (and ? 'fail' : 'pass')) decided = true;
    else if (r.state === 'unknown') unknown = true;
    else if (r.state === 'delayed') delayed = true;
  }
  if (decided) return verdict(and ? 'fail' : 'pass');
  if (unknown) return verdict('unknown', 'a condition inside cannot be previewed');
  if (delayed) return verdict('delayed', 'a condition inside waits for data');
  return verdict(and ? 'pass' : 'fail');
}

function evalCore(run: Run, node: DocumentLike, element: Obj | null, depth: number): LeafVerdict {
  const ctx = run.ctx;
  const d = (obj(node.data) ?? {}) as Obj;
  const key = node.template.startsWith(CONDITION_NS) ? node.template.slice(CONDITION_NS.length) : null;
  if (key === null) return verdict('unknown', 'not evaluated by the preview');
  switch (key) {
    case 'And':
    case 'Or':
    case 'Logic':
      return combine(run, arr(d.conditions).filter((c): c is string => typeof c === 'string'), element, depth, key !== 'Or');
    case 'Not': {
      if (typeof d.condition !== 'string') return verdict('fail', 'Not without a child evaluates False');
      const r = evalNode(run, d.condition, element, depth + 1);
      if (r.state === 'pass') return verdict('fail');
      if (r.state === 'fail') return verdict('pass');
      return verdict(r.state, r.reason);
    }
    case 'Empty':
      return bool(d.pass === true);
    case 'TimeRage': {
      const t = ctx.now;
      const start = instantOf(d.start);
      const finish = instantOf(d.finish);
      const repeat = toNumber(d.repeat) ?? 0;
      if (t < start) return verdict('fail', 'before the start');
      const cycle = repeat > 0 ? Math.floor((t - start) / repeat) : 0;
      const inside = start + cycle * repeat <= t && t < finish + cycle * repeat;
      return inside ? verdict('pass') : verdict('fail', repeat > 0 ? 'between cycles' : 'after the finish');
    }
    case 'TimeOfTheDay': {
      const notes: string[] = [];
      const start = minutesOfDay(d.start, 'start', notes);
      const finish = minutesOfDay(d.finish, 'finish', notes);
      const t = ctx.now;
      const minute = Math.floor((((t % DAY) + DAY) % DAY) / 60);
      let inside: boolean;
      if (start === finish) inside = false;
      else if (start < finish) inside = minute >= start && minute < finish;
      else inside = minute >= start || minute < finish; // overnight wraps midnight (Q-E-15)
      const reason = notes.length ? notes.join('; ') : start === finish ? 'an empty window never passes' : undefined;
      return verdict(inside ? 'pass' : 'fail', reason);
    }
    case 'DayOfTheWeek': {
      const mask = toNumber(d.days) ?? 0;
      const day = new Date(ctx.now * 1000).getUTCDay(); // Sunday = 0 → bit 1
      return bool((mask & (1 << day)) !== 0);
    }
    case 'ProfileFieldNumber':
    case 'ProfileFieldLong':
    case 'ProfileFieldInRange':
    case 'ProfileFieldString':
    case 'ProfileFieldBool':
    case 'ProfileFieldList':
      return evalProfile(run, key, d, element, depth);
    case 'SegmentCondition': {
      const seg = typeof d.segment === 'string' && d.segment ? d.segment : null;
      if (!seg) return verdict('fail', 'no segment chosen');
      const p = systemProfile(ctx);
      if (p === undefined) return verdict('delayed', 'the player has no system profile');
      const rec = arr(readPath(p, 'SegmentsInfo.Segments').value).map(obj).find((s) => s && refersTo(ctx, s.segment, seg));
      return rec ? bool(rec.isIn === true) : verdict('fail', 'never evaluated for this player');
    }
    case 'ActiveEvent':
    case 'ActiveOffer': {
      const field = key === 'ActiveEvent' ? 'gameEvent' : 'gameOffer';
      const target = typeof d[field] === 'string' && d[field] ? (d[field] as string) : null;
      if (!target) return verdict('fail', `no ${key === 'ActiveEvent' ? 'event' : 'offer'} chosen`);
      const p = systemProfile(ctx);
      if (p === undefined) return verdict('delayed', 'the player has no system profile');
      const list = arr(readPath(p, key === 'ActiveEvent' ? 'SmartInfo.GameEvents' : 'SmartInfo.GameOffers').value);
      // an EventInfo is active until isFinished; an OfferInfo (status Activated or Waiting) is removed when it ends
      const hit = list.map(obj).some((i) => i && refersTo(ctx, i[field], target) && (key === 'ActiveOffer' || i.isFinished !== true));
      return verdict(hit ? 'pass' : 'fail', hit ? undefined : 'no active instance');
    }
    case 'ABTestCondition': {
      const test = typeof d.test === 'string' && d.test ? d.test : null;
      if (!test) return verdict('fail', 'no test chosen');
      const p = systemProfile(ctx);
      if (p === undefined) return verdict('delayed', 'the player has no system profile');
      const rec = arr(readPath(p, 'TestsInfo.Tests').value).map(obj).find((t) => t && refersTo(ctx, t.test, test));
      const variant = typeof d.variant === 'string' && d.variant ? d.variant : null;
      if (!rec) return verdict('fail', 'not assigned to the test');
      if (!variant) return verdict('pass'); // an unresolved variant: assigned at all, finished included
      return refersTo(ctx, rec.variant, variant) ? verdict('pass') : verdict('fail', 'assigned to another variant');
    }
    case 'StoreItemWasPurchased': {
      const p = systemProfile(ctx);
      if (p === undefined) return verdict('delayed', 'the player has no system profile');
      const item = typeof d.storeItem === 'string' && d.storeItem ? d.storeItem : null;
      let count = 0;
      if (item) {
        const sells = (recorded: unknown) => {
          if (refersTo(ctx, recorded, item)) return true;
          if (typeof recorded !== 'string') return false;
          const offer = ctx.resolve(recorded); // an offer purchase counts for the store item its GameOffer sells
          return !!offer && refersTo(ctx, obj(offer.data)?.storeItem, item);
        };
        for (const list of ['Payments.Purchases', 'Payments.SoftPurchases']) for (const r of arr(readPath(p, list).value)) if (sells(obj(r)?.item)) count++;
      }
      return numberCompare(d.comparison, count, toNumber(d.value) ?? 0, `${count} purchase${count === 1 ? '' : 's'}`);
    }
    case 'ProductWasPurchased': {
      const ref = productRef(d.product);
      if (!ref) return verdict('fail', 'no product chosen');
      const p = systemProfile(ctx);
      if (p === undefined) return verdict('delayed', 'the player has no system profile');
      const ids = new Set([ref, ctx.product?.(ref)?.itemId].filter((x): x is string => typeof x === 'string' && x !== ''));
      const hit = arr(readPath(p, 'Payments.Purchases').value).some((r) => ids.has(String(obj(r)?.productId ?? '')));
      return bool(hit);
    }
    case 'InventoryItems': {
      const item = typeof d.item === 'string' && d.item ? d.item : null;
      if (!item) return verdict('fail', 'no item chosen');
      const p = systemProfile(ctx);
      if (p === undefined) return verdict('delayed', 'the player has no system profile');
      let total = 0;
      for (const inv of ['Currencies', 'Items', 'EventItems']) {
        for (const slot of arr(readPath(p, `Inventories.${inv}.Slots`).value)) {
          const it = obj(obj(slot)?.item);
          if (it && refersTo(ctx, it.item, item)) total += toNumber(it.a) ?? 0;
        }
      }
      return numberCompare(d.comparison, total, toNumber(d.value) ?? 0, `holds ${total}`);
    }
    case 'Revenue':
      return evalRevenue(ctx, d);
    case 'AppVersion': {
      if (!ctx.session.appVersion) return verdict('unknown', "needs the session's app version");
      return evalVersion(ctx.session.appVersion, d);
    }
    case 'InstallVersion': {
      const p = systemProfile(ctx);
      if (p === undefined) return verdict('delayed', 'the player has no system profile');
      const v = readPath(p, 'GeneralInfo.InstallVersion').value;
      if (typeof v !== 'string' || v === '') return verdict('delayed', 'the install version is empty');
      return evalVersion(v, d);
    }
    case 'EngineVersion': {
      if (!ctx.session.engineVersion) return verdict('unknown', "needs the session's engine version");
      const op = toNumber(d.comparison) ?? 0;
      if (op === 6) return verdict('fail', 'isNullOrEmpty on a number evaluates False');
      return bool(compare(op, ordinal(ctx.session.engineVersion, typeof d.value === 'string' ? d.value : ''), 0));
    }
    case 'Platform':
    case 'BalancyPlatform': {
      const fact = key === 'Platform' ? ctx.session.platform : ctx.session.balancyPlatform;
      if (fact === undefined || fact === '') return verdict('unknown', key === 'Platform' ? "needs the session's platform" : "needs the session's store platform");
      const a = toNumber(fact);
      const b = toNumber(d.value);
      return bool(a !== null && b !== null ? a === b : String(fact).toLowerCase() === String(d.value ?? '').toLowerCase());
    }
    case 'SystemLanguage': {
      if (!ctx.session.language) return verdict('unknown', "needs the session's language");
      const ref = typeof d.value === 'string' && d.value ? d.value : null;
      if (!ref) return verdict('fail', 'no language chosen');
      const doc = ctx.resolve(ref);
      if (!doc) return verdict('unknown', 'the language document has not loaded');
      const code = String(obj(doc.data)?.code ?? '');
      return bool(normalizeLanguage(code) === normalizeLanguage(ctx.session.language));
    }
    case 'Country': {
      if (!ctx.session.country) return verdict('unknown', "needs the session's country");
      return bool(ctx.session.country === String(d.code ?? ''));
    }
    default:
      return verdict('unknown', 'not evaluated by the preview');
  }
}

function normalizeLanguage(code: string): string {
  return code.trim().replace(/_/g, '-').toLowerCase();
}

function productRef(v: Json | undefined): string | null {
  if (typeof v === 'string') return v || null;
  const o = obj(v);
  const id = o?.productId ?? o?.id ?? o?.itemId;
  return typeof id === 'string' && id ? id : null;
}

/** A stored reference (a document id, or a legacy unnyId) names the leaf's document. */
function refersTo(ctx: EvalContext, recorded: unknown, id: string): boolean {
  if (typeof recorded !== 'string' && typeof recorded !== 'number') return false;
  const r = String(recorded);
  if (r === '') return false;
  if (r === id) return true;
  const doc = ctx.resolve(id);
  return !!doc?.unnyId && String(doc.unnyId) === r;
}

function systemProfile(ctx: EvalContext): Json | undefined {
  return ctx.profiles(SYSTEM_PROFILE);
}

function numberCompare(comparison: Json | undefined, actual: number, expected: number, what?: string): LeafVerdict {
  const op = toNumber(comparison) ?? 0;
  if (op === 6) return verdict('fail', 'isNullOrEmpty on a number evaluates False');
  return verdict(compare(op, actual, expected) ? 'pass' : 'fail', what);
}

function evalVersion(actual: string, d: Obj): LeafVerdict {
  const op = toNumber(d.comparison) ?? 0;
  const c = versionCompare(actual, d.value);
  if (c === null) return verdict('fail', 'unparsable version');
  if (op === 6) return verdict('fail', 'isNullOrEmpty on a version evaluates False');
  return verdict(compare(op, c, 0) ? 'pass' : 'fail', `version ${actual}`);
}

function evalRevenue(ctx: EvalContext, d: Obj): LeafVerdict {
  const type = toNumber(d.type) ?? 0;
  const days = toNumber(d.days) ?? 0;
  const p = systemProfile(ctx);
  if (p === undefined) return verdict('delayed', 'the player has no system profile');
  let measured = 0;
  if (type === 0 || type === 1) {
    const serverNow = ctx.serverNow ?? ctx.now;
    const from = serverNow - days * DAY;
    let sum = 0;
    let count = 0;
    if (days > 0) {
      for (const r of arr(readPath(p, 'Payments.Purchases').value)) {
        const o = obj(r);
        const time = toNumber(o?.time);
        if (!o || time === null || time < from || time > serverNow) continue;
        sum += toNumber(o.amount) ?? toNumber(o.priceUSD) ?? 0; // minor units; a legacy record counts its priceUSD
        count++;
      }
    }
    measured = type === 0 ? sum : count;
  } else if (days > 0) {
    // ads: the lifetime totals the profile keeps (AdsInfo has no per-view history, so the window cannot apply)
    const ads = obj(readPath(p, 'AdsInfo').value) ?? {};
    const sub = (name: string, f: 'revenue' | 'count') => toNumber(readPath(ads, `${name}.${f}`).value) ?? 0;
    switch (type) {
      case 2:
        measured = toNumber(readPath(ads, 'RevenueTotal').value) ?? 0;
        break;
      case 3:
        measured = sub('AdRewarded', 'count') + sub('AdInterstitial', 'count') + sub('AdCustom', 'count');
        break;
      case 4:
        measured = sub('AdRewarded', 'revenue');
        break;
      case 5:
        measured = sub('AdRewarded', 'count');
        break;
      case 6:
        measured = sub('AdInterstitial', 'revenue');
        break;
      case 7:
        measured = sub('AdInterstitial', 'count');
        break;
      case 8:
        measured = sub('AdCustom', 'revenue');
        break;
      case 9:
        measured = sub('AdCustom', 'count');
        break;
      default:
        measured = 0;
    }
  }
  return numberCompare(d.comparison, measured, toNumber(d.value) ?? 0, `measured ${measured}`);
}

/** The object a fullPath names: the list element inside a filter, the system profile, or a custom root by template name. */
function rootOf(ctx: EvalContext, profile: string, element: Obj | null): { root: Json | undefined; isElement: boolean } {
  const system = profile === '' || profile === SYSTEM_PROFILE || profile === shortName(SYSTEM_PROFILE);
  if (element) {
    if (profile === '' || (!system && ctx.profiles(profile) === undefined)) return { root: element, isElement: true };
  }
  return { root: system ? ctx.profiles(SYSTEM_PROFILE) : ctx.profiles(profile), isElement: false };
}

function evalProfile(run: Run, key: string, d: Obj, element: Obj | null, depth: number): LeafVerdict {
  const ctx = run.ctx;
  const fp = obj(d.fullPath);
  const profile = typeof fp?.profile === 'string' ? fp.profile : '';
  const path = typeof fp?.path === 'string' ? fp.path : '';
  if (!path) return verdict('fail', 'no field chosen');
  const { root, isElement } = rootOf(ctx, profile, element);
  if (root === undefined || root === null) {
    if (!isElement && !element) return verdict('delayed', `the player has no ${shortName(profile || SYSTEM_PROFILE)} profile`);
    return verdict('fail', `profile '${profile}' is not loaded`);
  }
  const { found, value } = readPath(root, path);
  switch (key) {
    case 'ProfileFieldNumber':
    case 'ProfileFieldLong': {
      if (!found || value === null || value === undefined) return verdict('fail', 'field missing');
      const n = toNumber(value);
      if (n === null) return verdict('fail', `field is ${typeof value}, not a number`);
      const op = toNumber(d.comparison) ?? 0;
      if (op === 6) return verdict('fail', 'isNullOrEmpty on a number evaluates False');
      const expected = toNumber(d.value) ?? 0;
      return verdict(compare(op, n, expected) ? 'pass' : 'fail', `value ${n}`);
    }
    case 'ProfileFieldInRange': {
      if (!found || value === null || value === undefined) return verdict('fail', 'field missing');
      const n = toNumber(value);
      if (n === null) return verdict('fail', `field is ${typeof value}, not a number`);
      const min = toNumber(d.min) ?? 0;
      const max = toNumber(d.max) ?? 0;
      if (max <= min) return verdict('fail', 'empty range');
      return verdict(n >= min && n < max ? 'pass' : 'fail', `value ${n}`);
    }
    case 'ProfileFieldString': {
      const text = typeof value === 'string' ? value : value === null || value === undefined ? null : typeof value === 'number' || typeof value === 'boolean' ? String(value) : null;
      const expected = typeof d.value === 'string' ? d.value : d.value === null || d.value === undefined ? '' : String(d.value);
      const op = toNumber(d.comparison) ?? 0;
      if (op === 6) return bool(text === null || text === '');
      if (op === 0) return bool(text === expected);
      if (op === 5) return bool(text !== expected);
      return bool(compare(op, ordinal(text ?? '', expected), 0));
    }
    case 'ProfileFieldBool':
      return bool(typeof value === 'boolean' && value === (d.value === true));
    case 'ProfileFieldList': {
      if (!found || !Array.isArray(value)) return verdict('fail', 'the field is not a list');
      const filter = typeof d.filter === 'string' ? d.filter : null;
      const inner = typeof d.condition === 'string' ? d.condition : null;
      const rows = value.map(obj).filter((r): r is Obj => r !== null); // scalar elements carry no fields
      const kept = filter ? rows.filter((r) => evalNode(run, filter, r, depth + 1).state === 'pass') : rows;
      const check = toNumber(d.checkType) ?? 0;
      if (check === 0) return numberCompare(d.comparison, kept.length, toNumber(d.value) ?? 0, `${kept.length} element${kept.length === 1 ? '' : 's'}`);
      if (check === 1) return bool(inner ? kept.some((r) => evalNode(run, inner, r, depth + 1).state === 'pass') : kept.length > 0);
      if (check === 2) return bool(kept.every((r) => (inner ? evalNode(run, inner, r, depth + 1).state === 'pass' : true)));
      return verdict('fail', `unknown check type ${check}`);
    }
    default:
      return verdict('unknown', 'not evaluated by the preview');
  }
}
